import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, StatusDot } from '../ui/Card.jsx';
import { ChevronRight, FileText, PhoneForwarded, Users, Power, PowerOff, Trash2, Loader2, Phone } from 'lucide-react';
import { coverFromSeed, ensureSeed, initialsOf } from './coverArt.js';
import { setAvatarEnabled } from '../../api/ivrAgents.js';
import { DeleteAvatarDialog } from './DeleteAvatarDialog.jsx';

/**
 * AvatarCard — AI 角色卡片
 *
 * 顯示封面、名稱、KB/角色類型(總機/客服/問卷)副資訊、啟用狀態。
 * 右上角 Power toggle:啟用/停用。停用後 sip-relay 不可綁 DID。
 * inProgress=true 時封面顯示「AI 進行中」spinner — 後端有 update SSE 還在跑。
 */
// dids: string[] = 綁了哪些門號（[] 是真的沒綁）；null = 還不知道（路由載入中／失敗）→ 什麼都不畫
export function AvatarCard({ avatar, inProgress, onUpdated, onDeleted, readOnly = false, dids = null }) {
    const { t } = useTranslation('ivrAgents');
    const [deleteOpen, setDeleteOpen] = useState(false);
    const seed = avatar.coverSeed || ensureSeed(avatar.avatarId);
    const initials = initialsOf(avatar.name);

    const isSurvey = avatar.newMode === 'survey' || avatar.surveyMode === true;
    const isTransferOnly = avatar.flowMode === 'transfer-only';
    const flowModeLabel = isSurvey ? t('flowMode.survey') : isTransferOnly ? t('flowMode.transferOnly') : t('flowMode.full');
    const coverKind = isSurvey ? 'survey' : isTransferOnly ? 'transfer-only' : 'full';
    const cover = coverFromSeed(seed, coverKind);
    const kbCount = (avatar.knowledgeBase || []).length;

    const [busy, setBusy] = useState(false);
    const [enabled, setEnabled] = useState(avatar.enabled === true);

    const handleToggle = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (busy) return;
        const next = !enabled;
        setBusy(true);
        try {
            await setAvatarEnabled(avatar.avatarId, next);
            setEnabled(next);
            onUpdated?.(avatar.avatarId, { enabled: next });
            // 通知 quota pill 重新計算（雖然 quota 目前沒 filter enabled，但放著未來相容）
            window.dispatchEvent(new CustomEvent('qubby:avatar-changed', { detail: { reason: 'toggle' } }));
        } catch (err) {
            console.error('[AvatarCard] toggle enabled failed:', err);
            alert(t('card.toggleFailed', { message: err.message }));
        } finally {
            setBusy(false);
        }
    };

    // 卡片一律可點進工作室（子帳號進去是唯讀、只能模擬通話）；readOnly 只隱藏啟用/刪除等編輯動作
    const Wrapper = ({ children }) => (
        <Link to={`/ivr-agents/${encodeURIComponent(avatar.avatarId)}`} className="block group">
            {children}
        </Link>
    );

    return (
        <>
        <Wrapper>
            <Card
                className={[
                    'hover:border-accent-300 transition group-hover:shadow-md p-0 overflow-hidden relative',
                    !enabled ? 'bg-cream-100/60' : '',
                ].join(' ')}
                padded={false}
            >
                {/* 封面 */}
                <div
                    className="h-32 flex items-center justify-center relative"
                    style={{ background: cover.gradient }}
                >
                    <div className="heading-display text-4xl font-semibold" style={{ color: cover.fg }}>
                        {initials}
                    </div>
                    {avatar.isSubAccount && (
                        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-white/80 text-[10px] font-mono uppercase tracking-wider text-ink-700">
                            {t('card.subAccount')}
                        </div>
                    )}
                    {inProgress && (
                        <div className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/90 text-accent-700 text-[11px] font-semibold shadow-sm">
                            <Loader2 size={12} className="animate-spin" />
                            {t('card.inProgress')}
                        </div>
                    )}
                </div>
                {/* 啟用/停用 toggle + 刪除 — 放 Card 層級不被 opacity-60 罩到，z 高在最前。子帳號不顯示 */}
                {!readOnly && (
                <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5">
                        <button
                            onClick={handleToggle}
                            disabled={busy}
                            title={enabled ? t('card.disableTitle') : t('card.enableTitle')}
                            className={[
                                'inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold transition shadow-sm',
                                enabled
                                    ? 'bg-white/90 text-success-500 hover:bg-white'
                                    : 'bg-white/90 text-danger-500 hover:bg-white',
                                busy ? 'opacity-50 cursor-wait' : 'cursor-pointer',
                            ].join(' ')}
                        >
                            {enabled
                                ? <Power size={12} strokeWidth={2.2} />
                                : <PowerOff size={12} strokeWidth={2.2} />}
                            {enabled ? t('card.enabled') : t('card.disabled')}
                        </button>
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (enabled) return;
                                setDeleteOpen(true);
                            }}
                            disabled={enabled}
                            title={enabled ? t('card.deleteDisabledTitle') : t('card.deleteTitle')}
                            className={[
                                'inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold transition shadow-sm',
                                enabled
                                    ? 'bg-white/60 text-ink-300 cursor-not-allowed opacity-50'
                                    : 'bg-danger-500 text-white hover:bg-danger-500/90 cursor-pointer ring-1 ring-danger-500/40',
                            ].join(' ')}
                        >
                            <Trash2 size={12} strokeWidth={2.2} />
                            {!enabled && <span>{t('common:actions.delete')}</span>}
                        </button>
                </div>
                )}

                {/* 內容 */}
                <div className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-2">
                        <h3 className="heading-display text-lg font-semibold text-ink-900 line-clamp-1">
                            {avatar.name}
                        </h3>
                        <ChevronRight
                            size={18}
                            className="text-ink-300 group-hover:text-accent-500 transition shrink-0 mt-1"
                        />
                    </div>

                    <div className="flex items-center gap-3 text-xs text-ink-500 mb-3">
                        <span className="inline-flex items-center gap-1">
                            {avatar.flowMode === 'transfer-only'
                                ? <PhoneForwarded size={12} strokeWidth={1.6} />
                                : <Users size={12} strokeWidth={1.6} />}
                            {flowModeLabel}
                        </span>
                        <span className="text-ink-300">·</span>
                        <span className="inline-flex items-center gap-1">
                            <FileText size={12} strokeWidth={1.6} />
                            {kbCount > 0 ? t('card.kbCount', { count: kbCount }) : t('card.noKb')}
                        </span>
                    </div>

                    {/* 綁定狀態 chips — SIP DID（tenant 隱藏：每個 avatar 只屬一個 tenant，顯示冗餘）
                        ⚠️ 資料來源是「進線路由」(sipPhoneNumbers)，不是 avatar.did ——
                        後者沒人寫、且裝不下一隻綁多支的情況。詳見 hooks/useTenantDids.js。*/}
                    <div className="flex flex-wrap items-center gap-1.5 mb-3 min-h-[22px]">
                        {/* null = 路由還沒回來 → 留白（min-h 撐住不跳版），絕不在不確定時畫「未綁門號」 */}
                        {dids === null ? null : dids.length > 0 ? (
                            <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success-100 text-success-500 text-[11px] font-mono border border-success-500/30"
                                title={t('card.boundDidTitle', { did: dids.join('、') })}
                            >
                                <Phone size={11} strokeWidth={2} />
                                {/* 多支只在 chip 顯示第一支 +N（chip 放不下），完整清單在 tooltip —— 免加 8 個語系的新 key */}
                                {t('card.boundDid', { did: dids.length > 1 ? `${dids[0]} +${dids.length - 1}` : dids[0] })}
                            </span>
                        ) : (
                            <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cream-100 text-ink-400 text-[11px] font-mono border border-cream-200"
                                title={t('card.unboundDidTitle')}
                            >
                                <Phone size={11} strokeWidth={2} />
                                {t('card.unboundDid')}
                            </span>
                        )}
                        {/* Live 標籤已移除：現行 avatar 全部跑 Gemini Live，標籤沒有辨識價值；
                            且它讀的是頂層 avatar.liveMode，非建立精靈開出來的角色沒有這欄位
                            （權威旗標是 ivrFlowConfig.liveMode），會讓同樣是 Live 的角色看起來不一樣。*/}
                    </div>

                    <div className="flex items-center justify-between">
                        <StatusDot
                            kind={enabled ? 'success' : 'idle'}
                            label={enabled ? t('card.statusBindable') : t('card.statusNotEnabled')}
                        />
                        {avatar.sharedAccountEmail ? (
                            <span
                                className="inline-block align-middle max-w-[10.5rem] truncate px-1.5 py-0.5 rounded-full bg-accent-50 text-accent-700 border border-accent-300/60 text-[10px] font-mono leading-tight"
                                title={t('card.sharedAccountTitle', { email: avatar.sharedAccountEmail })}
                            >
                                {t('card.sharedAccount', { email: avatar.sharedAccountEmail })}
                            </span>
                        ) : avatar.ownerEmail && (
                            <span className="font-mono text-[10px] text-ink-400 truncate max-w-[12rem]">
                                {avatar.ownerEmail}
                            </span>
                        )}
                    </div>
                </div>
            </Card>
        </Wrapper>
        {!readOnly && (
            <DeleteAvatarDialog
                open={deleteOpen}
                avatar={avatar}
                onClose={() => setDeleteOpen(false)}
                onDeleted={(id) => onDeleted?.(id)}
            />
        )}
        </>
    );
}
